import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { Edit, Refresh, Save } from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import { AppDispatch, RootState } from '../../../store/store';
import { fetchSystemSettings, updateSystemSetting } from '../../../store/slices/superAdminSlice';
import { SystemSettingsDto } from '../../../types/superAdminTypes';

const SystemSettingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { systemSettings, loading, error } = useSelector((state: RootState) => state.superAdmin);

  const [editingSetting, setEditingSetting] = useState<SystemSettingsDto | null>(null);
  const [editValue, setEditValue] = useState('');

  const fetchData = useCallback(() => { dispatch(fetchSystemSettings({})); }, [dispatch]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEdit = (setting: SystemSettingsDto) => {
    setEditingSetting(setting);
    setEditValue(setting.settingValue);
  };

  const handleSave = async () => {
    if (!editingSetting) return;
    try {
      await dispatch(updateSystemSetting({ id: editingSetting.id, data: { id: editingSetting.id, settingValue: editValue } })).unwrap();
      toast.success('Setting updated');
      setEditingSetting(null);
      fetchData();
    } catch (err: any) {
      toast.error(err || 'Failed to update setting');
    }
  };

  // Group settings by category
  const groupedSettings = systemSettings.reduce<Record<string, SystemSettingsDto[]>>((acc, s) => {
    const cat = s.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>System Settings</Typography>
          <Typography variant="body2" color="text.secondary">Configure platform-wide settings</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Skeleton variant="rounded" height={400} sx={{ borderRadius: 3 }} />
      ) : Object.keys(groupedSettings).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary">No system settings found</Typography>
        </Paper>
      ) : (
        Object.entries(groupedSettings).map(([category, settings]) => (
          <Paper key={category} sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="bold">{category}</Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Setting</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settings.map((setting) => (
                    <TableRow key={setting.id} hover>
                      <TableCell><Typography variant="body2" fontWeight={600}>{setting.settingKey}</Typography></TableCell>
                      <TableCell><Chip label={setting.settingValue} size="small" variant="outlined" /></TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{setting.description || '—'}</Typography></TableCell>
                      <TableCell>{new Date(setting.modifiedAt).toLocaleDateString()}</TableCell>
                      <TableCell align="center">
                        {setting.isEditable && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEdit(setting)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingSetting} onClose={() => setEditingSetting(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Edit Setting — {editingSetting?.settingKey}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {editingSetting?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{editingSetting.description}</Typography>
            )}
            <TextField fullWidth label="Value" value={editValue} onChange={(e) => setEditValue(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditingSetting(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSave} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemSettingsPage;
